import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Package, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { BESPOKE_COPY, GOOGLE_FORM_URL, IMAGES } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { GuideRequestButton } from "@/components/GuideRequest";
import { EnquiryModal } from "@/components/EnquiryModal";

const AUDIENCE_LABEL = { investor: "Investor", tourist: "Tourist", returnee: "Returnee & Relocator", expat: "Expat" };

const PaidGuideCard = ({ product }) => (
  <Link
    to={`/shop/p/${product._id}`}
    data-testid={`shop-paid-${product._id}`}
    className="group flex flex-col overflow-hidden border border-jawa-gold/40 bg-white transition-colors hover:border-jawa-coral"
  >
    {product.image_url && (
      <div className="relative h-44 overflow-hidden">
        <img src={product.image_url} alt={product.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
          Instant download
        </span>
      </div>
    )}
    <div className="flex flex-1 flex-col p-6">
      <h3 className="font-serif text-xl font-medium text-jawa-espresso">{product.title}</h3>
      <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-jawa-espresso/65">{product.description}</p>
      <div className="mt-5 flex items-center justify-between border-t border-jawa-stone pt-4">
        <span className="font-serif text-2xl font-medium text-jawa-espresso" data-testid={`shop-paid-price-${product._id}`}>
          <Price amount={product.price} from={product.currency || "GBP"} />
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral group-hover:text-jawa-espresso">
          Buy now <ArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  </Link>
);

const MerchCard = ({ product, onEnquire }) => (
  <article className="flex h-full flex-col overflow-hidden border border-jawa-espresso/12 bg-white transition-colors hover:border-jawa-gold/70" data-testid={`shop-merch-${product.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    {product.image_url && (
      <div className="h-44 overflow-hidden">
        <img src={product.image_url} alt={product.title} loading="lazy" className="h-full w-full object-cover" />
      </div>
    )}
    <div className="flex flex-1 flex-col justify-between p-6">
      <div>
        <h3 className="font-serif text-lg font-medium leading-snug text-jawa-espresso">{product.title}</h3>
        <p className="mt-2 text-xs leading-relaxed text-jawa-espresso/60">{product.description}</p>
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-jawa-stone pt-4">
        <span className="font-serif text-xl font-medium text-jawa-espresso">
          <Price amount={product.price} from={product.currency || "GBP"} />
        </span>
        <button
          onClick={() => onEnquire(product)}
          data-testid={`shop-merch-enquire-${product.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="bg-jawa-espresso px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
        >
          Enquire to order
        </button>
      </div>
    </div>
  </article>
);

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [enquiring, setEnquiring] = useState(null);

  useEffect(() => {
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const free = products.filter((p) => p.category === "free_airport" || p.category === "free_school");
  const paid = products.filter((p) => p.category === "guide" && p.price != null && p.checkout !== "enquiry");
  const collections = products.filter((p) => p.category === "collection");
  const vip = products.filter((p) => p.category === "vip");
  const merch = products.filter((p) => p.category === "merch");

  return (
    <div className="bg-jawa-ivory" data-testid="shop-page">
      <Nav />
      <Seo
        title="Shop — A–Z Guides, Collections & Free Downloads | JÀWÀ"
        description="JÀWÀ digital downloads: free Nigerian airport and school guides, members-only A–Z collections and curated resources for your African journey."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="The JÀWÀ Shop" title="Guides, plans and journeys." />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            Digital guides and memberships first, bespoke experiences second, curated African goods
            when the collection is ready. Everything is labelled honestly — instant downloads,
            membership benefits, or bespoke planning.
          </p>
        </FadeUp>

        <FadeUp className="mt-16">
          <div className="flex items-center gap-3">
            <Download className="h-5 w-5 text-jawa-turquoise" />
            <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">Free digital downloads</h2>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="shop-free-products">
            {free.map((p) => (
              <article key={p._id} className="flex flex-col overflow-hidden border border-jawa-turquoise/40 bg-white" data-testid={`shop-free-${p.category}`}>
                <div className="relative h-44">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    loading="lazy"
                    onError={(e) => {
                      if (p.category === "free_airport" && !e.currentTarget.dataset.fallback) {
                        e.currentTarget.dataset.fallback = "1";
                        e.currentTarget.src = "/lagos-airport-terminal.jpg";
                      }
                    }}
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute left-4 top-4 bg-jawa-turquoise px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-jawa-nearblack">Free download</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-xl font-medium text-jawa-espresso">{p.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-jawa-espresso/65">{p.description}</p>
                  <GuideRequestButton
                    kind={p.category === "free_airport" ? "airport" : "school"}
                    label="Get the Free Guide"
                    testid={`shop-download-${p.category}`}
                    className="mt-5 inline-flex w-fit items-center gap-2 bg-jawa-espresso px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory hover:bg-jawa-coral"
                  />
                </div>
              </article>
            ))}
          </div>
        </FadeUp>

        {paid.length > 0 && (
          <FadeUp className="mt-16">
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-jawa-coral" />
              <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">Paid guides</h2>
            </div>
            <p className="mt-3 max-w-xl text-sm text-jawa-espresso/60">
              One-off PDF guides — pay securely online and your download begins immediately.
            </p>
            <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="shop-paid-products">
              {paid.map((p) => (
                <PaidGuideCard key={p._id} product={p} />
              ))}
            </div>
          </FadeUp>
        )}

        <FadeUp className="mt-20">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-jawa-coral" />
            <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">A–Z Guide collections</h2>
          </div>
          <p className="mt-3 max-w-xl text-sm text-jawa-espresso/60">
            Membership benefits — members-only online collections, included with each JÀWÀ membership from £4.99/month.
          </p>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-collections">
            {collections.map((p) => (
              <Link
                key={p._id}
                to={`/membership/${p.audience}`}
                data-testid={`shop-collection-${p.audience}`}
                className="group flex cursor-pointer flex-col overflow-hidden border border-jawa-gold/40 bg-jawa-espresso"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 border border-jawa-gold/60 bg-jawa-nearblack/70 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-jawa-gold">
                    Membership benefit
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-serif text-lg font-medium text-jawa-ivory">{AUDIENCE_LABEL[p.audience]} A–Z</h3>
                  <span className="mt-4 inline-flex w-fit items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral group-hover:text-jawa-ivory">
                    View membership <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </FadeUp>

        <FadeUp className="mt-20">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-jawa-gold" />
            <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">Experiences & bespoke planning</h2>
          </div>
          <p className="mt-3 max-w-xl text-sm text-jawa-espresso/60">
            Service enquiries — priced per plan after we understand your trip. An enquiry is a conversation, not a booking.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="shop-experiences">
            {[
              { t: "Couples & celebrations", d: "Honeymoons, anniversaries, birthdays and date-perfect Lagos itineraries." },
              { t: "Family & group journeys", d: "Multi-generational holidays, friend groups and child-friendly plans." },
              { t: "Detty December planning", d: "Arrival, events, glam, transport and safety — the season, handled." },
            ].map((x) => (
              <article key={x.t} className="flex flex-col border border-jawa-stone bg-white p-7" data-testid={`shop-experience-${x.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-serif text-xl font-medium text-jawa-espresso">{x.t}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-jawa-espresso/65">{x.d}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.15em] text-jawa-espresso/45">Priced per plan — bespoke service</p>
                <a
                  href={GOOGLE_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shop-bespoke-${x.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="mt-5 inline-flex w-fit items-center gap-1.5 bg-jawa-coral px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-jawa-espresso"
                >
                  Request a plan <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </article>
            ))}
          </div>
          <p className="mt-6 max-w-xl text-xs leading-relaxed text-jawa-espresso/50">{BESPOKE_COPY}</p>
        </FadeUp>

        {vip.length > 0 && (
          <FadeUp className="mt-20">
            <div className="border border-jawa-gold/40 bg-jawa-espresso p-8 lg:p-10" data-testid="shop-vip-section">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-jawa-gold">JÀWÀ VIP Arrival Experience</p>
                  <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-jawa-ivory">Arrive like you're expected.</h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-jawa-ivory/65">
                    Four arrival packages — from the thoughtful Welcome Box to the full Private Client Welcome with
                    chauffeur-driven pickup and airport concierge assistance. Every package includes seven days of
                    WhatsApp concierge support from your arrival. Book online below; gift contents are confirmed with
                    you before fulfilment.
                  </p>
                </div>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {vip.map((p) => (
                  <Link
                    key={p._id}
                    to={`/shop/p/${p._id}`}
                    data-testid={`shop-vip-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex h-full flex-col overflow-hidden border border-jawa-ivory/15 text-left transition-colors hover:border-jawa-gold/60"
                  >
                    {p.image_url && (
                      <span className="block h-36 w-full overflow-hidden">
                        <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </span>
                    )}
                    <span className="flex flex-1 flex-col justify-between p-5">
                      <span className="font-serif text-lg leading-snug text-jawa-ivory group-hover:text-jawa-gold">{p.title}</span>
                      <span className="mt-4 flex items-center justify-between">
                        <span className="font-serif text-lg text-jawa-gold">
                          <span className="mr-1 text-xs">from</span>
                          <Price amount={p.price} from={p.currency || "USD"} className="text-jawa-gold" />
                          <span className="ml-1 text-[10px] uppercase tracking-[0.15em] text-jawa-ivory/40">pp</span>
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-jawa-gold" />
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
              <div className="mt-8 border border-jawa-ivory/15 p-6" data-testid="shop-vip-support-block">
                <p className="font-serif text-xl text-jawa-gold">Your first week in Nigeria, with JÀWÀ in your corner.</p>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-jawa-ivory/65">
                  Every package includes seven days of WhatsApp concierge support from your agreed arrival or service
                  start date — practical settling-in questions, restaurant and local-service recommendations, transport
                  guidance and help booking JÀWÀ experiences. Support hours 08:00–20:00 WAT. Third-party costs, extra
                  transfers, tickets and professional advice aren't included — we always quote and agree before arranging
                  anything paid.
                </p>
              </div>
            </div>
          </FadeUp>
        )}

        {merch.length > 0 && (
          <FadeUp className="mt-20">
            <div className="flex items-center gap-3">
              <Package className="h-5 w-5 text-jawa-gold" />
              <h2 className="font-serif text-3xl font-medium tracking-tight text-jawa-espresso">The JÀWÀ Collection</h2>
            </div>
            <p className="mt-3 max-w-xl text-sm text-jawa-espresso/60">
              Curated Nigerian lifestyle and travel goods — the JÀWÀ tee and tote, travel accessories, original A4
              artwork by credited Nigerian artists and gift bundles. Small batches and made-to-order: enquire and we
              confirm availability, exact contents and delivery before you pay.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-merch-grid">
              {merch.map((p) => (
                <MerchCard key={p._id} product={p} onEnquire={setEnquiring} />
              ))}
            </div>
          </FadeUp>
        )}

        <FadeUp className="mt-20">
          <div className="flex flex-wrap items-center justify-between gap-6 border border-jawa-turquoise/40 bg-white p-8" data-testid="shop-lynksim">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.3em] text-jawa-turquoise">Partner pick — connectivity</p>
              <h2 className="mt-2 font-serif text-2xl font-medium text-jawa-espresso">Travel eSIMs by LynkSim</h2>
              <p className="mt-2 text-sm leading-relaxed text-jawa-espresso/65">
                Data sorted before you land — browse roaming eSIMs at lynksim.com. Checkout completes on LynkSim's own
                website. JÀWÀ customers receive 15% off qualifying products — your discount code is revealed after any
                JÀWÀ purchase.
              </p>
            </div>
            <a
              href="https://www.lynksim.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shop-lynksim-link"
              className="inline-flex items-center gap-2 bg-jawa-turquoise px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-nearblack transition-colors hover:bg-jawa-espresso hover:text-jawa-ivory"
            >
              Shop at lynksim.com <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </FadeUp>
      </main>
      <Footer />
      {enquiring && (
        <EnquiryModal
          journey={enquiring.category === "vip" ? "VIP Arrival Experience" : `Shop — ${enquiring.title}`}
          title={enquiring.title}
          arrival={enquiring.category === "vip"}
          onClose={() => setEnquiring(null)}
          testid="shop-enquiry-modal"
        />
      )}
    </div>
  );
}
